import { useState, type ReactElement } from "react";
import {
  MARKDOWN_TEXT_LIMITS,
  type MarkdownTextParams,
} from "@playbookai/widget-schemas";
import { Input } from "../../atoms/Input/Input.js";
import { SegmentedControl } from "../../atoms/SegmentedControl/SegmentedControl.js";
import { Textarea } from "../../atoms/Textarea/Textarea.js";
import type { WidgetBodyProps } from "../types.js";
import { useFieldEditSession } from "../field-edit-session.js";
import { MarkdownContent } from "./MarkdownContent.js";

type EditorMode = "edit" | "preview";

/** Edit or preview a Text card, rejecting oversized titles and keeping title/body edit leases separate. */
export function MarkdownTextExpandedEditor({
  data,
  onChange,
  readOnly,
}: WidgetBodyProps<MarkdownTextParams>): ReactElement {
  const disabled = readOnly === true;
  const [mode, setMode] = useState<EditorMode>(disabled ? "preview" : "edit");
  const [titleError, setTitleError] = useState<string | undefined>();
  const activeMode = disabled ? "preview" : mode;
  // PB-4923 AC5. Two independent leaves in the merge map, so two claims — the
  // body is one last-save-wins field and the title is another, and somebody
  // retitling must not look like they are holding the body.
  const titleLease = useFieldEditSession("title");
  const markdownLease = useFieldEditSession("markdown");

  return (
    <div className="mx-auto flex min-h-0 w-full max-w-5xl flex-col gap-5 p-5 text-pb-text">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold">Text card</h2>
          <p className="mt-1 text-sm text-pb-text-muted">
            Capture a narrative, summary, or mixed framework result.
          </p>
        </div>
        <SegmentedControl<EditorMode>
          value={activeMode}
          options={[
            { value: "edit", label: "Edit", disabled },
            { value: "preview", label: "Preview" },
          ]}
          onChange={setMode}
        />
      </div>

      {activeMode === "edit" ? (
        <div className="grid min-h-0 gap-4">
          <Input
            label="Title"
            hint={`Up to ${MARKDOWN_TEXT_LIMITS.titleCharacters} characters.`}
            {...(titleError ? { error: titleError } : {})}
            value={data.title}
            disabled={disabled}
            {...titleLease}
            onChange={(event) => {
              const title = event.target.value;
              if (title.length > MARKDOWN_TEXT_LIMITS.titleCharacters) {
                setTitleError(
                  `The title exceeds ${MARKDOWN_TEXT_LIMITS.titleCharacters} characters. Shorten the pasted title and try again; it hasn’t been saved.`,
                );
                return;
              }
              setTitleError(undefined);
              onChange({ ...data, title });
            }}
          />
          <Textarea
            label="Markdown"
            hint="Supports headings, lists, links, tables, task lists, and code blocks."
            showCount
            maxLength={MARKDOWN_TEXT_LIMITS.markdownCharacters}
            value={data.markdown}
            disabled={disabled}
            variant="code"
            className="min-h-[50vh]"
            {...markdownLease}
            onChange={(event) =>
              onChange({ ...data, markdown: event.target.value })
            }
          />
        </div>
      ) : (
        <section
          aria-label="Markdown preview"
          className="min-h-[50vh] overflow-auto rounded-lg border border-pb-border bg-pb-card p-5"
        >
          <h3 className="mb-4 text-xl font-semibold">
            {data.title || "Untitled"}
          </h3>
          {data.markdown.trim() ? (
            <MarkdownContent markdown={data.markdown} />
          ) : (
            <p className="text-sm text-pb-text-muted">
              Nothing to preview yet.
            </p>
          )}
        </section>
      )}
    </div>
  );
}
